import { Options } from 'blip-ds/dist/types/components/datepicker/datepicker-interface';
import { ALL_GROUPS_ID, NO_GROUP_ID } from 'constants/utils';
import i18n from 'i18n/i18n';
import { WorkGroup } from '../typings/services/WorkGroup';

/**
 * Creates dropdown options from work groups, including a "no group" option
 */
export const createGroupOptions = (groups: WorkGroup[]): Options[] => {
    const NO_GROUP_OPTION = { label: i18n.t('go-personal-group-filter-none'), value: NO_GROUP_ID };
    const ALL_GROUPS_OPTION = { label: i18n.t('go-personal-group-filter-all'), value: ALL_GROUPS_ID };

    const groupOptions = groups.map(group => ({
        label: group.workGroupName,
        value: group.workGroupId,
    }));

    return [ALL_GROUPS_OPTION, NO_GROUP_OPTION, ...groupOptions];
};

/**
 * Checks if a work group has a parent group
 */
export const hasParentGroup = (group: WorkGroup): boolean => {
    return group.parentWorkGroupId !== null && group.parentWorkGroupId !== undefined;
};

/**
 * Builds a map of direct parent-child relationships from work groups
 */
export const buildParentChildHierarchy = (groups: WorkGroup[]): Map<number, number[]> => {
    const parentToChildrenMap = new Map<number, number[]>();

    // Build direct parent-child relationships
    groups.forEach(group => {
        if (hasParentGroup(group)) {
            const parentId = group.parentWorkGroupId as number;

            if (!parentToChildrenMap.has(parentId)) {
                parentToChildrenMap.set(parentId, []);
            }

            const children = parentToChildrenMap.get(parentId);
            if (children) {
                children.push(group.workGroupId);
            }
        }
    });

    return parentToChildrenMap;
};

export const getAllDescendantIds = (parentId: number, parentToChildrenMap: Map<number, number[]>): number[] => {
    const directChildren = parentToChildrenMap.get(parentId) || [];
    const allDescendants = [...directChildren];

    // Recursively get all descendants
    directChildren.forEach(childId => {
        const grandchildren = getAllDescendantIds(childId, parentToChildrenMap);
        allDescendants.push(...grandchildren);
    });

    return allDescendants;
};

/**
 * Creates a complete hierarchy map showing all descendants for each parent group
 */
export const createGroupHierarchy = (groups: WorkGroup[]): Map<number, number[]> => {
    const parentToChildrenMap = buildParentChildHierarchy(groups);
    const hierarchyMap = new Map<number, number[]>();

    // For each parent, get all descendant IDs (children, grandchildren, etc.)
    parentToChildrenMap.forEach((_, parentId) => {
        const allDescendants = getAllDescendantIds(parentId, parentToChildrenMap);
        hierarchyMap.set(parentId, allDescendants);
    });

    return hierarchyMap;
};

/**
 * Main function to create group options and hierarchy from work groups
 */
export const createGroups = (groups: WorkGroup[]): { groups: Options[]; groupsOfGroups: Map<number, number[]> } => {
    const groupOptions = createGroupOptions(groups);
    const groupHierarchy = createGroupHierarchy(groups);

    return {
        groups: groupOptions,
        groupsOfGroups: groupHierarchy,
    };
};
